<template>
	<div class="input-wrapper">
		<!-- Kaal <input>: TextInput geeft alleen zijn id door aan het echte veld, niet data-testid/name/type. -->
		<input
			:id="`${codeName}-code`"
			ref="fullCodeInput"
			:data-testid="`${codeName}-code`"
			:maxlength="amountOfInputs"
			:name="`${codeName}-code`"
			aria-hidden="true"
			autocomplete="off"
			class="full-code-input"
			tabindex="-1"
			@change="applyFullCode($event.target.value)"
			@input="applyFullCode($event.target.value)"/>
		<template v-for="inputId in amountOfInputs" :key="inputId">
			<text-input
				:id="`${codeName}-digit-${inputId}`"
				:force-focus="forceInputFocus[inputId]"
				:force-select="forceInputFocus[inputId]"
				:max-length="1"
				:model-value="inputValues[inputId]"
				:placeholder="placeholderObject[inputId]"
				:size="SizeOptions.L"
				class="input-overwrite"
				@keyup.backspace.exact.prevent="focusPrevious(inputId)"
				@keyup.arrow-left.exact.prevent="focusPrevious(inputId)"
				@keyup.arrow-right.exact.prevent="focusNext(inputId)"
				@update:modelValue="updateModelValue(inputId, $event)"/>
		</template>
	</div>
</template>
<script>
import {SizeOptions, TextInput} from "@datapas/vue-input";

export default {
	name: "AuthCodeInput",
	components: {
		TextInput,
	},
	emits: [
		'authCode',
		'keyup',
		'firstTimeCompletion'
	],
	props: {
		placeholder: {
			type: String,
			default: ''
		},
		amountOfInputs: {
			type: Number,
			required: true,
		},
		codeName: {
			type: String,
			default: 'auth'
		},
	},
	data() {
		return {
			SizeOptions,
			inputValues: {},
			forceInputFocus: {},
			isCompletedFirstTime: false
		};
	},
	computed: {
		authcode() {
			return Object.values(this.inputValues).join("");
		},
		placeholderValues() {
			const placeholderPerInput = {};

			const placeholderInputOffset = Math.floor((Math.max(this.amountOfInputs, this.placeholder.length) - this.placeholder.length) / 2);
			for (let index = 0; index < this.amountOfInputs; index++) {
				const placeholderIndex = (index - placeholderInputOffset);
				if (placeholderIndex >= 0 && placeholderIndex < this.placeholder.length) {
					placeholderPerInput[index + 1] = this.placeholder[placeholderIndex];
				}
			}

			return placeholderPerInput;
		},
		placeholderObject() {
			if (this.authcode) {
				return {};
			} else {
				return this.placeholderValues;
			}
		}
	},
	created() {
		for (let index = 1; index <= this.amountOfInputs; index++) {
			this.forceInputFocus[index] = 0;
		}
		this.clearInputValues();
	},
	mounted() {
		this.forceInputFocus[1]++;
		this.watchFullCodeInput();
	},
	beforeUnmount() {
		this.fullCodeObserver.disconnect();
	},
	methods: {
		emitAuthCode(code) {
			this.$emit('authCode', code);
		},
		emitFirstTimeCompletion() {
			this.$emit('firstTimeCompletion');
		},
		updateModelValue(inputId, value) {
			if (!value) {
				this.inputValues[inputId] = null;
			} else if (value?.length > 1) {
				// Zorgt er voor dat je een gehele string kan plakken.
				let stringArrayValue = value.split("");
				let nextFocus = stringArrayValue.length > this.amountOfInputs - 1 ? this.amountOfInputs - 1 : stringArrayValue.length;
				for (let loopIndex = inputId; loopIndex <= Math.min(stringArrayValue.length, this.amountOfInputs); loopIndex++) {
					this.inputValues[loopIndex] = stringArrayValue[loopIndex - inputId];
				}
				this.focusNext(nextFocus);
			} else {
				this.inputValues[inputId] = value;
				this.focusNext(inputId);
			}

			if (this.authcode.length === this.amountOfInputs) {
				this.emitAuthCode(this.authcode);
				if (!this.isCompletedFirstTime) {
					this.emitFirstTimeCompletion();
					this.isCompletedFirstTime = true;
				}
			} else {
				this.emitAuthCode(null);
			}
		},
		clearInputValues() {
			for (let index = 1; index <= this.amountOfInputs; index++) {
				this.inputValues[index] = null;
			}
		},
		focusPrevious(inputId) {
			if (inputId >= 1) {
				this.forceInputFocus[inputId - 1]++;
			}
		},
		focusNext(inputId) {
			if (inputId < this.amountOfInputs) {
				this.forceInputFocus[inputId + 1]++;
			}
		},
		// Drie vulroutes dekken: @input/@change vangen fill()/typen, de observer setAttribute('value'), de value-setter element.value.
		watchFullCodeInput() {
			const fullCodeInput = this.$refs.fullCodeInput;

			// Leest het value-attribuut, niet .value: na de dirty value flag volgt .value het attribuut niet meer.
			this.fullCodeObserver = new MutationObserver(() => this.applyFullCode(fullCodeInput.getAttribute('value')));
			this.fullCodeObserver.observe(fullCodeInput, {attributeFilter: ['value']});

			const valueProperty = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
			Object.defineProperty(fullCodeInput, 'value', {
				configurable: true,
				get: () => valueProperty.get.call(fullCodeInput),
				set: (value) => {
					valueProperty.set.call(fullCodeInput, value);
					this.applyFullCode(value);
				}
			});
		},
		applyFullCode(value) {
			const code = (value ?? '').replace(/\s/g, '').slice(0, this.amountOfInputs);
			// Dedupe: fill() vuurt input én change en elke niet-lege authCode submit; zonder dit een dubbele submit.
			if (code === this.authcode) {
				return;
			}

			// Wissen vóór het vullen: een kortere of lege waarde laat anders de oude trailing digits staan.
			this.clearInputValues();
			this.updateModelValue(1, code);
		},
	}
};
</script>
<style lang="scss" scoped>
.input-wrapper {
	position: relative;
}

// Geen display:none of visibility:hidden - tooling vult geen veld dat het als onzichtbaar ziet.
// left/top expliciet, anders landt het midden op een zichtbaar veld en vangt daar een klik.
.full-code-input {
	height: 1px;
	left: 0;
	opacity: 0;
	position: absolute;
	top: 0;
	width: 1px;
}

.input-overwrite {
	max-width: 35px;

	& :deep(.html-input input) {
		text-align: center;
	}
}
</style>